import type { ResponseInterface } from "@/libs/server/goApiServer/common/commonInterface";
import type {
  ExperienceDetailInterface,
  RequestDetailExperienceInterface,
} from "@/libs/server/goApiServer/experience/ExperienceServiceInterface/getDetailExperience";
import { ResponseExperienceDataInterface } from "@/libs/server/goApiServer/experience/ExperienceServiceInterface/getDetailExperience";
import type {
  RequestGetEventAvailableInterface,
  RequestGetTicketAvailableInterface,
} from "@/libs/server/goApiServer/experience/ExperienceServiceInterface/getEventTicketAvailableInterfaces";
import type { RequestGetLandingSectionInterface } from "@/libs/server/goApiServer/experience/ExperienceServiceInterface/getLandingSectionInterface";
import { ResponseGetLandingSectionClass } from "@/libs/server/goApiServer/experience/ExperienceServiceInterface/getLandingSectionInterface";
import type {
  ExperienceEventSeoInterfaces,
  RequestEventSeoExperienceInterface,
} from "@/libs/server/goApiServer/experience/ExperienceServiceInterface/getSeoExperience";
import { ResponseExperienceEventSeoInterface } from "@/libs/server/goApiServer/experience/ExperienceServiceInterface/getSeoExperience";
import type { ResponseGetTicketAvailableInterface } from "@/libs/server/goApiServer/experience/ExperienceServiceInterface/getTicketAvailableV3Interface/getTicketAvailableV3Interface";
import { ResponseGetTicketAvailable } from "@/libs/server/goApiServer/experience/ExperienceServiceInterface/getTicketAvailableV3Interface/getTicketAvailableV3Interface";
import type { RequestCategoryTabs } from "@/libs/server/goApiServer/experience/ExperienceServiceInterface/RequestCategoryTabs";
import type { RequestExperienceGetTypeInterface } from "@/libs/server/goApiServer/experience/ExperienceServiceInterface/RequestExperienceGetTypeInterface";
import type { RequestRecommendExperienceSEO } from "@/libs/server/goApiServer/experience/ExperienceServiceInterface/RequestExperienceRecommendInterface";
import type { RequestExperienceTopCity } from "@/libs/server/goApiServer/experience/ExperienceServiceInterface/RequestExperienceTopCity";
import type { RequestExperienceTopCountryInterface } from "@/libs/server/goApiServer/experience/ExperienceServiceInterface/RequestExperienceTopCountry";
import { ResponseCategoryTabs } from "@/libs/server/goApiServer/experience/ExperienceServiceInterface/ResponseCategoryTabs";
import { ResponseDestination } from "@/libs/server/goApiServer/experience/ExperienceServiceInterface/ResponseDestination";
import { ResponseExperienceGetTypeInterface } from "@/libs/server/goApiServer/experience/ExperienceServiceInterface/ResponseExperienceGetTypeInterface";
import { ResponseExperiencePopularInterface } from "@/libs/server/goApiServer/experience/ExperienceServiceInterface/ResponseExperiencePopularInterface";
import { ResponseExperienceRecommendInterface } from "@/libs/server/goApiServer/experience/ExperienceServiceInterface/ResponseExperienceRecommendInterface";
import { ResponseTopCity } from "@/libs/server/goApiServer/experience/ExperienceServiceInterface/ResponseExperienceTopCity";
import { ResponseExperienceTopCountryClasses } from "@/libs/server/goApiServer/experience/ExperienceServiceInterface/ResponseExperienceTopCountryInterface";
import { ResponseExperienceTopDestinationInterface } from "@/libs/server/goApiServer/experience/ExperienceServiceInterface/ResponseExperienceTopDestinationInterface";
import { ResponseGetExperienceExclusivePresent } from "@/libs/server/goApiServer/klub/KlubServiceInterface/getExperienceExclusive";
import { generateParamsEventExp } from "@/utils/common/other/generateParamsEventExp";
import { apiRequest } from "@/utils/common/serviceApiHelper/serviceApiUtils";

import { EventAllocateData } from "./experienceEventAllowcateInterface";
import { EventPreAllocateData } from "./experienceEventPreAllowcateInterface";
import {
  ExperienceEventRecentlyViewResponse,
  type RequestExperienceEventInterface,
} from "./experienceEventRecentlyViewInterface";
import { ExperienceRecentlyViewResponse } from "./experienceRecentlyViewInterface";
// import experienceSearchResultMock from "../../mocks/experiences/searchResultResponse.json";
import { ExperienceSearchResultResponse } from "./experienceSearchResultResponse";
import { ResponseTicket } from "./getTicketInterface";
import { ResponseAllocate } from "./postAttractionAllocateInterface";
import {
  ResponsePreAllocate,
  ResponsePreAllocateV3,
} from "./postPreAllocateInterface";

class ExperienceServiceApi {
  private baseUrl: string;

  private basePath: string;

  private basePathV3: string;

  private basePathKlub: string;

  private basePathDes: string;

  private basePathKlubV3: string;

  private basePathV5: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.basePath = "v2/experience";
    this.basePathV3 = "v3/experience";
    this.basePathKlub = "v2/klub";
    this.basePathKlubV3 = "v3/klub";
    this.basePathDes = "v2/destination";
    this.basePathV5 = "v5/experience";
  }

  // ******************************************************
  // ****************** Method: GET ***********************
  // ******************************************************

  async getLandingSection(params: RequestGetLandingSectionInterface) {
    try {
      const queryParams = new URLSearchParams({
        sectionType: params.sectionType,
      });

      const response = await apiRequest(
        this.baseUrl,
        `${this.basePathV5}/Landing?${queryParams}`,
        "GET",
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        false,
      );
      return new ResponseGetLandingSectionClass(response as any);
    } catch (error) {
      return null;
    }
  }

  async getLandingPage(params: RequestExperienceGetTypeInterface) {
    const queryParams = new URLSearchParams({ url: params.url });

    const response = await apiRequest(
      this.baseUrl,
      `${this.basePath}/getLandingPage?${queryParams}`,
      "GET",
    );
    // const response = experienceSearchResultMock;
    return new ResponseExperienceGetTypeInterface(response);
  }

  async getCategoryTabs(
    params?: RequestCategoryTabs,
  ): Promise<ResponseCategoryTabs> {
    const queryParams = new URLSearchParams({
      categorySlug: params?.categorySlug || "",
    });
    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePathV3}/categories?${queryParams}`,
      "GET",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      true,
    )) as any;
    try {
      return new ResponseCategoryTabs(response);
    } catch (error) {
      return new ResponseCategoryTabs({});
    }
  }

  async getEventSeoExperience(
    params: RequestEventSeoExperienceInterface,
  ): Promise<ExperienceEventSeoInterfaces | null> {
    const queryParams = new URLSearchParams(params as any);

    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePath}/seoCategory?${queryParams}`,
      "GET",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      true,
    )) as any;
    try {
      return new ResponseExperienceEventSeoInterface(response);
    } catch (error) {
      return null;
    }
  }

  async getExperienceRecommend(params: RequestRecommendExperienceSEO | string) {
    try {
      const queryParams = new URLSearchParams(params as any);
      const response = (await apiRequest(
        this.baseUrl,
        `${this.basePathV3}/Recommend?${queryParams}`,
        "GET",
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        false,
      )) as any;
      return new ResponseExperienceRecommendInterface(response);
    } catch (error) {
      return new ResponseExperienceRecommendInterface({});
    }
  }

  async getTopCountry(params: RequestExperienceTopCountryInterface) {
    const queryParams = new URLSearchParams({
      categorySlug: params.categorySlug,
    });
    try {
      const response = await apiRequest(
        this.baseUrl,
        `${this.basePath}/topCountry?${queryParams}`,
        "GET",
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        false,
      );
      return new ResponseExperienceTopCountryClasses(response);
    } catch (error) {
      throw new Error("getTopCountry Function Service Failed");
    }
  }

  async getTopCity(params: RequestExperienceTopCity) {
    try {
      const queryParams = new URLSearchParams(params as any);

      const response = (await apiRequest(
        this.baseUrl,
        `${this.basePath}/topCity?${queryParams}`,
        "GET",
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        false,
      )) as any;
      return new ResponseTopCity(response);
    } catch (error) {
      throw error;
    }
  }

  async getEventAvailable(
    params: RequestGetEventAvailableInterface,
  ): Promise<ResponseInterface<ResponseGetTicketAvailableInterface> | null> {
    const queryParams = new URLSearchParams(params as any);
    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePath}/eventAvailable?${queryParams}`,
      "GET",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      false,
    )) as any;
    try {
      return JSON.parse(
        JSON.stringify(new ResponseGetTicketAvailable(response)),
      );
    } catch (error) {
      return null;
    }
  }

  async getTicketAvailable(
    params: RequestGetTicketAvailableInterface,
  ): Promise<ResponseInterface<ResponseGetTicketAvailableInterface> | null> {
    const queryParams = new URLSearchParams(params as any);

    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePath}/available?${queryParams}`,
      "GET",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      false,
    )) as any;
    try {
      return JSON.parse(
        JSON.stringify(new ResponseGetTicketAvailable(response)),
      );
    } catch (error) {
      return null;
    }
  }

  // NOTE -> api experience get search result
  async getExperienceSearchResult(params: Record<string, any>) {
    const queryParams = new URLSearchParams(params);
    const urlAPI = `${this.basePathV3}/SearchDestination?${queryParams}`;
    const response = await apiRequest(this.baseUrl, urlAPI, "GET");
    // const response = experienceSearchResultMock;
    try {
      return new ExperienceSearchResultResponse(response);
    } catch (error) {
      return new ExperienceSearchResultResponse();
    }
  }

  async getDetailExperience(
    params: RequestDetailExperienceInterface,
  ): Promise<ExperienceDetailInterface | null> {
    const queryParams = new URLSearchParams(params as any);
    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePath}/detail?${queryParams}`,
      "GET",
    )) as any;
    try {
      return JSON.parse(
        JSON.stringify(new ResponseExperienceDataInterface(response)),
      );
    } catch (error) {
      return null;
    }
  }

  async getExperienceRecentlyView(params: number[]): Promise<any> {
    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePath}/getExperienceRecently?experienceIds=[${params}]`,
      "GET",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      false,
    )) as any;
    return new ExperienceRecentlyViewResponse(response);
  }

  async getExperienceEventRecentlyView(
    params: RequestExperienceEventInterface[],
  ): Promise<any> {
    const slugs = generateParamsEventExp(params);
    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePath}/getEventRecently?slugs=[${slugs}]`,
      "GET",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      false,
    )) as any;
    return new ExperienceEventRecentlyViewResponse(response);
  }

  // ******************************************************
  // ****************** Method: POST **********************
  // ******************************************************

  async postEventPreAllocate(body: any) {
    try {
      const response = (await apiRequest(
        this.baseUrl,
        `${this.basePath}/EventPreAllocate`,
        "POST",
        body,
      )) as any;
      return new EventPreAllocateData(response);
    } catch (error) {
      return null;
    }
  }

  async postEventAllocate(body: any) {
    try {
      const response = (await apiRequest(
        this.baseUrl,
        `${this.basePath}/eventAllocate`,
        "POST",
        body,
      )) as any;
      return new EventAllocateData(response);
    } catch (error) {
      return null;
    }
  }

  async postPreAllocate(body: any) {
    try {
      const response = (await apiRequest(
        this.baseUrl,
        `${this.basePath}/PreAllocate`,
        "POST",
        body,
      )) as any;
      return new ResponsePreAllocate(response);
    } catch (error) {
      return null;
    }
  }

  async postPreAllocateV3(body: any) {
    try {
      const response = (await apiRequest(
        this.baseUrl,
        `${this.basePathV3}/PreAllocate`,
        "POST",
        body,
      )) as any;
      return new ResponsePreAllocateV3(response);
    } catch (error) {
      return null;
    }
  }

  async postExperiencesAllocate(body: any) {
    const allocateBaseUrl =
      process.env.NEXT_PUBLIC_ALLOCATE_BASE_URL || this.baseUrl;

    try {
      const response = (await apiRequest(
        allocateBaseUrl,
        `${this.basePath}/allocate`,
        "POST",
        body,
      )) as any;
      return new ResponseAllocate(response);
    } catch (error) {
      return null;
    }
  }

  async postExperiencesAllocateV3(body: any) {
    const allocateBaseUrl =
      process.env.NEXT_PUBLIC_ALLOCATE_BASE_URL || this.baseUrl;

    try {
      const response = (await apiRequest(
        allocateBaseUrl,
        `${this.basePathV3}/allocate`,
        "POST",
        body,
      )) as any;
      return new ResponseAllocate(response);
    } catch (error) {
      return null;
    }
  }

  async getTicket(params: string) {
    const queryParams = new URLSearchParams(params as any);
    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePath}/AirlineBySlug?${queryParams}`,
      "GET",
    )) as any;
    return new ResponseTicket(response);
  }

  async getExperiencePopular(params: string) {
    try {
      const queryParams = new URLSearchParams(params);

      const response = (await apiRequest(
        this.baseUrl,
        `${this.basePathV3}/Popular?${queryParams}`,
        "GET",
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        false,
      )) as any;
      return new ResponseExperiencePopularInterface(response);
    } catch (error) {
      throw error;
    }
  }

  async getExperienceExclusive(params: string) {
    try {
      const queryParams = new URLSearchParams(params);
      const response = (await apiRequest(
        this.baseUrl,
        `${this.basePathKlubV3}/ExperienceExclusive?${queryParams}`,
        "GET",
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        false,
      )) as any;

      return new ResponseGetExperienceExclusivePresent(response);
    } catch (error) {
      throw error;
    }
  }

  async getExperienceTopDestination(params: string) {
    try {
      const queryParams = new URLSearchParams(params);

      const response = (await apiRequest(
        this.baseUrl,
        `${this.basePathDes}/topDestination?${queryParams}`,
        "GET",
      )) as any;

      return new ResponseExperienceTopDestinationInterface(response);
    } catch (error) {
      throw error;
    }
  }

  async getExperienceDestination(): Promise<ResponseDestination> {
    try {
      const response = (await apiRequest(
        this.baseUrl,
        `${this.basePath}/tourist`,
        "GET",
      )) as any;
      const mapData = new ResponseDestination(response);
      return mapData;
    } catch (error) {
      return new ResponseDestination();
    }
  }
}

export default ExperienceServiceApi;
